import React from "react";
import Card from "./Card";
import Header from "./Header";

const About = () => {
  return (
    <div className="bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="relative">
        <div className="h-[60vh] relative overflow-hidden">
          {/* Background Image with Parallax Effect */}
          <div
            className="absolute inset-0 bg-cover bg-fixed bg-center"
            style={{ 
              backgroundImage: `url('./assets/images/others/coffee.jpg')`,
              transform: "translateZ(0)"
            }}
          ></div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-emerald-900/60"></div>

          {/* Content */}
          <Header />
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="max-w-3xl text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight drop-shadow-lg animate-fadeIn">
                About Us
              </h1>
              <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
              <p className="text-xl md:text-2xl text-white font-light leading-relaxed max-w-2xl mx-auto">
                <span className="italic border-l-4 border-emerald-500 pl-4">
                  "Growing through modern agriculture"
                </span>
              </p>
            </div>
          </div>
          
          {/* Decorative Wave Divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
              <path fill="#ffffff" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
            </svg>
          </div>
        </div>

        {/* Company Overview Section */}
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-16">
              <div className="md:w-1/2">
                <div className="mb-16 relative">
                  <span className="text-sm font-medium text-emerald-600 tracking-wider uppercase mb-3 block">About Our Company</span>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                    Why Choose Us?
                  </h2>
                  <div className="prose prose-lg text-gray-600">
                    <p className="text-lg leading-relaxed border-l-4 border-emerald-500 pl-6">
                      Palnet Industrial Trading PLC is a dynamic and emerging
                      agricultural company established in 2024 and headquartered in
                      Addis Ababa, Ethiopia. We are dedicated to providing high-quality
                      agricultural products and contributing to the modernization of
                      the Ethiopian agricultural sector.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-8">
                  <div 
                    className="group"
                    data-aos="fade-up"
                    data-aos-delay="100"
                  >
                    <div className="flex items-start space-x-6 p-6 border-2 border-gray-100 hover:border-emerald-500 transition-colors duration-300 rounded-lg bg-white">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg border-2 border-emerald-500 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Commitment to High-Quality Products</h3>
                        <p className="text-gray-600">Palnet is dedicated to delivering premium-grade oilseeds, cereals, spices, and coffee meeting high international standards.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className="group"
                    data-aos="fade-up"
                    data-aos-delay="200"
                  >
                    <div className="flex items-start space-x-6 p-6 border-2 border-gray-100 hover:border-emerald-500 transition-colors duration-300 rounded-lg bg-white">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg border-2 border-emerald-500 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Experienced Leadership Team</h3>
                        <p className="text-gray-600">The company is led by seasoned professionals with extensive expertise in finance, agriculture, export, and technology, ensuring strong strategic direction.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className="group"
                    data-aos="fade-up"
                    data-aos-delay="300"
                  >
                    <div className="flex items-start space-x-6 p-6 border-2 border-gray-100 hover:border-emerald-500 transition-colors duration-300 rounded-lg bg-white">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg border-2 border-emerald-500 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Focus on Reliable International Partnerships</h3>
                        <p className="text-gray-600">Palnet prioritizes building robust and enduring relationships with global clients, fostering trust and long-term collaboration.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="md:w-1/2">
                <div className="sticky top-8">
                  <div className="relative mb-12">
                    <div className="absolute inset-0 border-2 border-emerald-500 rounded-lg transform translate-x-4 translate-y-4"></div>
                    <img 
                      src="./assets/images/others/logo_grid.jpg" 
                      alt="PalNet Logo" 
                      className="relative z-10 rounded-lg border-2 border-emerald-500 object-cover h-[400px] w-full bg-white"
                      onError={(e) => {e.target.src = './coffee.jpg'}}
                    />
                    <div className="absolute -bottom-6 z-10 -left-6 bg-white border-2 border-emerald-500 p-6 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">Established 2024</p>
                      <p className="text-emerald-600">Addis Ababa, Ethiopia</p>
                    </div>
                  </div>
                  
                  <div className="space-y-8">
                    <div 
                      className="group"
                      data-aos="fade-up"
                      data-aos-delay="400"
                    >
                      <div className="flex items-start space-x-6 p-6 border-2 border-gray-100 hover:border-emerald-500 transition-colors duration-300 rounded-lg bg-white">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-lg border-2 border-emerald-500 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Consistent Supply Chain</h3>
                          <p className="text-gray-600">Its direct agricultural production ensures a more reliable and consistent supply of essential commodities.</p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className="group"
                      data-aos="fade-up"
                      data-aos-delay="500"
                    >
                      <div className="flex items-start space-x-6 p-6 border-2 border-gray-100 hover:border-emerald-500 transition-colors duration-300 rounded-lg bg-white">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-lg border-2 border-emerald-500 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Value-Adding Agro-Processing</h3>
                          <p className="text-gray-600">Palnet's ambition to develop modern agro-processing plants indicates a commitment to enhancing product value and meeting diverse market needs.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Cards Section */}
        <div className="bg-gray-50 py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <Card />
            </div>
          </div>
        </div>

        {/* Leadership Section */}
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our Leadership Team
              </h2>
              <div className="w-24 h-1 bg-emerald-500 mx-auto mb-6"></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Meet the visionaries driving our mission to revolutionize agriculture in Ethiopia
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                {
                  name: "Banteyrga Kebede",
                  role: "CEO",
                  description:
                    "A seasoned business leader with over 20 years of experience across finance, agriculture, manufacturing, sales, and telecommunications.",
                  image: "./assets/images/others/profile2.png",
                },
                {
                  name: "Habib Mohammed",
                  role: "Deputy CEO & CFO",
                  description:
                    "A highly accomplished financial executive with over 20 years of experience in finance and banking.",
                  image: "./assets/images/others/profile2.png",
                },
                {
                  name: "Tewodros Mekonen",
                  role: "COO",
                  description:
                    "A dynamic leader with over 20 years of experience in sales, marketing, technology, manufacturing, and telecommunications.",
                  image: "./assets/images/others/profile2.png",
                },
              ].map((leader, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transform transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="bg-emerald-600 h-24 relative">
                    <div className="absolute left-1/2 transform -translate-x-1/2 top-12">
                      <img
                        src={leader.image}
                        alt={`${leader.name} profile`}
                        className="w-24 h-24 rounded-full border-4 border-white object-cover"
                      />
                    </div>
                  </div>
                  <div className="pt-16 p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1 text-center">
                      {leader.name}
                    </h3>
                    <p className="text-emerald-600 font-medium mb-4 text-center">
                      {leader.role}
                    </p>
                    <div className="w-12 h-1 bg-emerald-500 mx-auto mb-4"></div>
                    <p className="text-gray-600 leading-relaxed text-center">
                      {leader.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-emerald-700 py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Grow with Us?
              </h2>
              <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
                Join us in our mission to revolutionize agriculture in Ethiopia through innovation, quality, and sustainability.
              </p>
              <a 
                href="/contact" 
                className="inline-block bg-white text-emerald-700 font-bold py-3 px-8 rounded-full hover:bg-emerald-50 transition-colors duration-300 shadow-lg"
              >
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;